/**
 * The generic resource processor: one cache, in-flight map and event emitter,
 * parameterised by how a resource is fetched and processed. The load transitions
 * live in `resourceProcessorLoad.ts` and the invalidation in `resourceProcessorClear.ts`.
 */
import { LRUCache } from './LRUCache';
import { parseSubResourcePath, resourceFilePath } from './subResourcePath';
import { createLoadLane } from './resourceProcessorLoad';
import { createClearCache } from './resourceProcessorClear';
import { CachedFailure, DEFAULT_MAX_ENTRIES } from './resourceProcessorTypes';
import * as logger from '../logger';
/**
 * Ceiling on waiting for a peer processor's address (a material's texture, a font's
 * `base_font`), far above any real fetch: a dependency that never arrives fails the
 * dependent instead of parking it. Shared so `ResourceLoader.peerRequire` and the font
 * processor's loader cannot drift apart.
 */
export const PEER_LOAD_TIMEOUT_MS = 30_000;
/**
 * Create a resource processor with caching, deduplication and event emission.
 * Configure exactly one fetch mode: `fileEventBus` with `process` (bytes, then
 * process) and an optional `shouldProcess`, or `loadDirectly` for a load that
 * fetches for itself, such as a scene or a resource read from a peer's cache.
 */
export function createResourceProcessor(config) {
    const { fileEventBus, eventBus, resourceType, readsFile, shouldProcess, process, addressesSubResources = false, loadDirectly, dispose, } = config;
    // Capacity eviction disposes like `clearCache()`, but skips a cached failure.
    const cache = new LRUCache(config.maxEntries ?? DEFAULT_MAX_ENTRIES, (_path, value) => {
        if (value && !(value instanceof CachedFailure) && dispose)
            dispose(value);
    });
    // A load owns its path's entry through a unique token. A clear removes the entry,
    // so a load that completes under the cleared provider state finds its token gone
    // and is disposed, never cached or announced. A later request loads fresh.
    const inflight = new Map();
    const { finishLoad, fail, handleFileLoaded, handleFileFailed } = createLoadLane({
        cache,
        inflight,
        eventBus,
        resourceType,
        fileEventBus,
        shouldProcess,
        process,
        dispose,
    });
    const clearCache = createClearCache({ cache, inflight, eventBus, resourceType });
    if (fileEventBus) {
        fileEventBus.on('loaded', handleFileLoaded);
        fileEventBus.on('failed', handleFileFailed);
    }
    return {
        request(path) {
            // A cache never holds `undefined`, so one read answers both "held" and "what".
            const cached = cache.get(path);
            if (cached !== undefined) {
                if (cached instanceof CachedFailure) {
                    logger.info(`[${resourceType}Processor] Cache hit (failed) for: ${path}`);
                    eventBus.emit(resourceType, 'failed', path, cached.error);
                }
                else {
                    logger.info(`[${resourceType}Processor] Cache hit for: ${path}`);
                    eventBus.emit(resourceType, 'loaded', path, cached);
                }
                return;
            }
            if (inflight.has(path)) {
                logger.info(`[${resourceType}Processor] Already loading: ${path}`);
                return;
            }
            const { filePath, subResourceId } = parseSubResourcePath(path);
            if (subResourceId !== undefined && !addressesSubResources) {
                // Refused before any fetch: reading a sub-resource is a property of `process`,
                // not of the bytes. Answering here also covers `loadDirectly` mode and cannot
                // leave the address stuck in flight, as a post-arrival check can.
                fail(path, new Error(`${resourceType} processor cannot address the sub-resource ` +
                    `"${subResourceId}" inside ${filePath}`));
                return;
            }
            if (readsFile && !readsFile(filePath)) {
                fail(path, new Error(`${resourceType} processor does not read ${filePath}`));
                return;
            }
            inflight.set(path, Symbol(path));
            eventBus.emit(resourceType, 'requested', path);
            if (loadDirectly) {
                void finishLoad(path, () => loadDirectly(path));
            }
            else if (fileEventBus) {
                // The byte layer, and the provider, watcher and **Resource upload**s behind
                // it, only sees real files: a sub-resource's bytes are its owning file's.
                fileEventBus.request(resourceFilePath(path));
            }
            else {
                inflight.delete(path);
                eventBus.emit(resourceType, 'failed', path, new Error(`No fetch mode configured for ${resourceType}: ${path}`));
            }
        },
        getCached(path) {
            const cached = cache.get(path);
            return cached instanceof CachedFailure ? null : cached;
        },
        failure(path) {
            const cached = cache.peek(path);
            return cached instanceof CachedFailure ? cached.error : undefined;
        },
        isCached(path) {
            return cache.has(path);
        },
        isLoading(path) {
            return inflight.has(path);
        },
        clearCache,
        release(path) {
            // A failure stays: it is small, and it is what stops a retry.
            if (cache.isPinned(path) || cache.peek(path) instanceof CachedFailure)
                return;
            cache.delete(path);
        },
        getCacheSize() {
            return cache.size;
        },
        cachedPaths() {
            return [...cache.keys()];
        },
        inflightPaths() {
            return [...inflight.keys()];
        },
        pin(path) {
            cache.pin(path);
        },
        unpin(path) {
            cache.unpin(path);
        },
    };
}
//# sourceMappingURL=createResourceProcessor.js.map